
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { FileValidationService } from './FileValidationService';
import { BucketService } from './BucketService';
import { SettingsService } from './SettingsService';
import { FileValidationResult, StorageSettings, UploadResult } from './types';

export class StorageService {
  /**
   * Upload a file to storage
   */
  static async uploadFile(
    file: File,
    bucket: string,
    path: string = ''
  ): Promise<UploadResult | null> {
    try {
      // Prepare the file path
      const fileName = `${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
      const filePath = path ? `${path}/${fileName}` : fileName;

      // Upload the file
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Upload error:', error);
        toast.error(`Upload failed: ${error.message}`);
        return null;
      }

      // Get the public URL
      const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(data.path);

      return {
        path: data.path,
        url: urlData.publicUrl
      };
    } catch (error) {
      console.error('Upload exception:', error);
      toast.error('Upload failed due to an unexpected error');
      return null;
    }
  }

  /**
   * Delete a file from storage
   */
  static async deleteFile(bucket: string, path: string): Promise<boolean> {
    try {
      const { error } = await supabase.storage
        .from(bucket)
        .remove([path]);

      if (error) {
        console.error('Delete error:', error);
        toast.error(`Delete failed: ${error.message}`);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Delete exception:', error);
      toast.error('Delete failed due to an unexpected error');
      return false;
    }
  }

  /**
   * Get the public URL for a file
   */
  static getPublicUrl(bucket: string, path: string): string {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path);
    
    return data.publicUrl;
  }

  /**
   * List files in a bucket
   */
  static async listFiles(bucket: string, path: string = ''): Promise<string[]> {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .list(path);

      if (error) {
        console.error('List files error:', error);
        return [];
      }

      return data.map(item => item.name);
    } catch (error) {
      console.error('List files exception:', error);
      return [];
    }
  }

  // Expose methods from other services
  static validateFile = FileValidationService.validateFile;
  static validateFileWithOptions = FileValidationService.validateFileWithOptions;
  static ensureRequiredBuckets = BucketService.ensureRequiredBuckets;
  static checkBucketExists = BucketService.checkBucketExists;
  static createBucket = BucketService.createBucket;
  static getStorageSettings = SettingsService.getStorageSettings;
}

// For backward compatibility
export default StorageService;
